import { useEffect, useRef } from 'react';

//miranda tokens
import { getToken } from '@loadsmart/miranda-tokens';

const GAIN = 3.8;

const VERTEX =
  'attribute vec2 aPos;void main(){gl_Position=vec4(aPos,0.0,1.0);}';

// "Helix Haze": a slow, fibrous haze tinted with the brand green.
const FRAGMENT = `
precision mediump float;
uniform vec2 uRes;uniform float uTime;uniform float uSpeed;uniform float uK;uniform vec3 uC0;
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float noise(vec2 p){vec2 i=floor(p);vec2 f=fract(p);vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(hash(i),hash(i+vec2(1.0,0.0)),u.x),mix(hash(i+vec2(0.0,1.0)),hash(i+vec2(1.0,1.0)),u.x),u.y);}
float fbm(vec2 p){float v=0.0;float a=0.5;for(int i=0;i<4;i++){v+=a*noise(p);p*=2.03;a*=0.5;}return v;}
void main(){
  vec2 uv=gl_FragCoord.xy/uRes-0.5;uv.x*=uRes.x/uRes.y;
  float s=uTime*uSpeed;
  float ang=-0.22;mat2 R=mat2(cos(ang),-sin(ang),sin(ang),cos(ang));
  vec2 p=R*uv;float a=p.x;float b=p.y;
  float radius=0.34+0.05*sin(a*2.1+s*0.1);
  float nb=clamp(b/radius,-1.0,1.0);float surf=asin(nb);
  float fiber=fbm(vec2(a*3.2-s*0.12,(surf*2.4+a*2.6+s*0.2)*1.6));
  fiber=pow(fiber,3.2);
  float rim=1.0-sqrt(max(0.0,1.0-nb*nb));
  float body=smoothstep(1.15,0.85,abs(b/radius));
  float i=fiber*body*(0.55+0.45*rim);
  float haze=fbm(vec2(a*1.1+4.0,b*1.8-s*0.06))*exp(-abs(b)*2.2)*0.06;
  vec3 col=uC0*(i*0.38+haze);
  col=1.0-exp(-col*1.6);
  float gr=hash(gl_FragCoord.xy+floor(uTime*5.0)*vec2(13.7,7.3));
  col*=0.93+0.14*gr;col+=(gr-0.5)*0.02;
  gl_FragColor=vec4(max(col,0.0)*uK,1.0);}`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    return null;
  }

  return shader;
}

// Decorative WebGL background. Pauses while off screen or in a hidden tab and
// renders a single still frame under prefers-reduced-motion.
export function HeroFx({ intensity = 0.55, phase = 0, className }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext('webgl', {
      antialias: false,
      alpha: false,
      powerPreference: 'low-power',
    });

    if (!gl) {
      return undefined;
    }

    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);

    if (!vertex || !fragment) {
      return undefined;
    }

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return undefined;
    }

    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    );
    const position = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniform = (name) => gl.getUniformLocation(program, name);
    const [red, green, blue] = getToken('color-brand')
      .split(',')
      .map((channel) => Number(channel) / 255);

    gl.uniform1f(uniform('uSpeed'), 1);
    gl.uniform1f(uniform('uK'), intensity * GAIN);
    gl.uniform3f(uniform('uC0'), red, green, blue);

    const uRes = uniform('uRes');
    const uTime = uniform('uTime');
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const lowPower =
      window.matchMedia('(pointer: coarse)').matches ||
      window.innerWidth <= 820;
    const dpr = lowPower ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
    const start = performance.now() - phase * 1000;

    let frameId = 0;
    let visible = true;

    const render = (time) => {
      gl.uniform2f(uRes, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform1f(uTime, time);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const frame = () => {
      render((performance.now() - start) / 1000);
      frameId = requestAnimationFrame(frame);
    };

    const play = () => {
      if (!frameId && !reduced && visible && !document.hidden) {
        frameId = requestAnimationFrame(frame);
      }
    };

    const pause = () => {
      cancelAnimationFrame(frameId);
      frameId = 0;
    };

    const resize = () => {
      if (!canvas.clientWidth || !canvas.clientHeight) {
        return;
      }

      canvas.width = Math.round(canvas.clientWidth * dpr);
      canvas.height = Math.round(canvas.clientHeight * dpr);
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      render(reduced ? 2.5 : (performance.now() - start) / 1000);
    };

    const onVisibility = () => (document.hidden ? pause() : play());

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const intersectionObserver = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      return visible ? play() : pause();
    });
    intersectionObserver.observe(canvas);

    document.addEventListener('visibilitychange', onVisibility);
    resize();
    play();

    return () => {
      pause();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intensity, phase]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
